import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { UserProfile } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { 
  Sparkles, 
  Orbit, 
  Compass, 
  TreePine, 
  Flame, 
  Mountain, 
  ShieldAlert, 
  Droplets, 
  CheckCircle2, 
  BookOpen, 
  BrainCircuit, 
  ShieldCheck, 
  HelpCircle, 
  ChevronRight,
  SunMoon,
  Star
} from 'lucide-react';

interface DashboardProps {
  userProfile: UserProfile | null;
  onSaveProfile: (profile: UserProfile, redirectTo?: string) => void;
  onClearProfile: () => void;
  onAnalysisComplete?: (data: any) => void;
  onSaveProfileForDev?: (profile: UserProfile, shouldNavigate?: boolean) => void;
  existingData?: any;
  isLoading?: boolean;
}

const Dashboard: React.FC<DashboardProps> = ({ 
  userProfile, 
  onSaveProfile, 
  onClearProfile, 
  onSaveProfileForDev, 
  existingData, 
  isLoading 
}) => {
  const navigate = useNavigate();
  const { session, dbUser } = useAuth();
  const [devClickCount, setDevClickCount] = useState(0);
  const [consentChecked, setConsentChecked] = useState(false);
  const [activeElementInfo, setActiveElementInfo] = useState<string>('wood');

  useEffect(() => {
    document.title = 'Cosmic Saju — Free AI Korean Astrology & Four Pillars of Destiny Reading';
    // 페이지 진입 시 데이터 초기화
    onClearProfile();
  }, []);

  const [formData, setFormData] = useState<Partial<UserProfile>>({
    name: '',
    birthYear: '1990',
    birthMonth: '1',
    birthDay: '1',
    birthTime: 'unknown',
    isLunar: false,
    isLeap: false,
    gender: 'female',
    reportLanguage: 'English',
  });

  const [timeParts, setTimeParts] = useState({ hour: '12', minute: '00', period: 'AM' });
  const [timeUnknown, setTimeUnknown] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const isPremiumWithSaving = !!(session && !session.user?.is_anonymous && dbUser && (dbUser.credits || 0) >= 40);
    if (isPremiumWithSaving && !consentChecked) {
      alert('Please agree to the privacy policy and data collection terms to proceed.');
      return;
    }
    
    let formattedTime = 'unknown';
    if (!timeUnknown) {
      let h = parseInt(timeParts.hour);
      if (timeParts.period === 'PM' && h < 12) h += 12;
      if (timeParts.period === 'AM' && h === 12) h = 0;
      formattedTime = `${h.toString().padStart(2, '0')}:${timeParts.minute}`;
    }
    
    const finalFormData = { 
      ...formData, 
      birthTime: formattedTime,
      timeUnknown: timeUnknown
    };

    onSaveProfile(finalFormData as UserProfile, '/report');
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => currentYear - i);
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const days = Array.from({ length: 31 }, (_, i) => i + 1);

  if (isLoading) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center min-h-[60vh] gap-6">
        <div className="relative size-24">
          <div className="absolute inset-0 rounded-full border-t-2 border-primary animate-spin"></div>
          <div className="absolute inset-2 rounded-full border-r-2 border-emerald-500 animate-[spin_1.5s_linear_infinite_reverse]"></div>
          <div className="absolute inset-4 rounded-full border-b-2 border-amber-500 animate-[spin_2s_linear_infinite]"></div>
        </div>
        <div className="flex flex-col items-center">
          <h3 className="text-xl font-bold bg-gradient-to-r from-primary via-emerald-500 to-amber-500 bg-clip-text text-transparent animate-pulse">
            Consulting the Stars...
          </h3>
          <p className="text-slate-500 dark:text-text-secondary mt-2 text-sm">Decoding your cosmic DNA & astrological coordinates</p>
        </div>
      </div>
    );
  }

  if (existingData) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center min-h-[60vh] gap-6 p-4">
        <h2 className="text-3xl font-black text-slate-800 dark:text-white">Your Stars Have Aligned</h2>
        <p className="text-slate-600 dark:text-slate-300">We've decoded your cosmic blueprint.</p>
        <Link 
          to="/report"
          className="px-8 py-4 bg-primary hover:bg-primary-dark text-white rounded-2xl font-bold shadow-xl transition-all block text-center"
        >
          View My Report
        </Link>
      </div>
    );
  }

  const elementDetails: Record<string, { title: string; hanja: string; color: string; desc: string; keywords: string; traits: string }> = {
    wood: {
      title: 'Wood',
      hanja: '木 (목)',
      color: 'text-emerald-500',
      keywords: 'Growth • Vision • Empathy • Innovation',
      desc: 'Wood represents the energy of springtime, upward expansion, and humanitarian goodwill. People with strong Wood energy are natural pioneers and compassionate mentors.',
      traits: 'Ideal Careers: Entrepreneurship, Education, Environmental Tech, Creative Writing, Medicine.'
    },
    fire: {
      title: 'Fire',
      hanja: '火 (화)',
      color: 'text-rose-500',
      keywords: 'Passion • Charisma • Expression • Clarity',
      desc: 'Fire embodies midsummer radiance, warmth, and expressive communication. High Fire charts bring magnetic stage presence, aesthetic intuition, and rapid action.',
      traits: 'Ideal Careers: Media, Performing Arts, Marketing, Artificial Intelligence, High Fashion.'
    },
    earth: {
      title: 'Earth',
      hanja: '土 (토)',
      color: 'text-amber-500',
      keywords: 'Stability • Trust • Neutrality • Grounding',
      desc: 'Earth is the balancing mediator between seasons. It provides rock-solid reliability, maternal patience, and profound organizational capacity.',
      traits: 'Ideal Careers: Real Estate, Financial Planning, Conflict Mediation, Operations Management, Architecture.'
    },
    metal: {
      title: 'Metal',
      hanja: '金 (금)',
      color: 'text-slate-400',
      keywords: 'Justice • Precision • Boundaries • Mastery',
      desc: 'Metal represents crisp autumn air, analytical sharpness, and structural discipline. People with high Metal energy possess impeccable standards and decisiveness.',
      traits: 'Ideal Careers: Law, Financial Analysis, Engineering, Surgery, High-Tech Hardware, Auditing.'
    },
    water: {
      title: 'Water',
      hanja: '水 (수)',
      color: 'text-blue-500',
      keywords: 'Wisdom • Flow • Intuition • Adaptability',
      desc: 'Water embodies winter stillness, philosophical wisdom, and endless emotional depth. Strong Water energy grants extraordinary adaptability and strategic foresight.',
      traits: 'Ideal Careers: Philosophy, Strategic Consulting, Psychology, Global Logistics, Deep Research.'
    }
  };

  return (
    <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 py-12 md:py-20 animate-[fadeIn_0.5s_ease-out] space-y-16">
      
      {/* Top Hero Header */}
      <header className="text-center space-y-4 max-w-3xl mx-auto">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-black tracking-wider uppercase">
          <Sparkles className="w-3.5 h-3.5" />
          <span>Authentic Korean Saju × Generative AI</span>
        </div>
        <h1 
          onClick={() => {
            const newCount = devClickCount + 1;
            setDevClickCount(newCount);
            if (newCount >= 5) {
              if (onSaveProfileForDev) {
                let formattedTime = 'unknown';
                if (!timeUnknown) {
                  let h = parseInt(timeParts.hour);
                  if (timeParts.period === 'PM' && h < 12) h += 12;
                  if (timeParts.period === 'AM' && h === 12) h = 0;
                  formattedTime = `${h.toString().padStart(2, '0')}:${timeParts.minute}`;
                }
                const devProfile = {
                  ...formData,
                  birthTime: formattedTime,
                  timeUnknown: timeUnknown
                } as UserProfile;
                onSaveProfileForDev(devProfile);
              }
              setDevClickCount(0);
            }
          }}
          className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-slate-900 dark:text-white leading-tight select-none"
        >
          Discover Your <span className="bg-gradient-to-r from-primary via-purple-500 to-pink-500 bg-clip-text text-transparent">Cosmic DNA</span>
        </h1>
        <p className="text-base sm:text-lg text-slate-650 dark:text-slate-350 leading-relaxed font-medium">
          Enter your birth date and exact time to calculate your Four Pillars of Destiny (사주팔자). Unlock profound insights into your innate strengths, career destiny, relationship synergy, and 10-year luck cycles.
        </p>
      </header>

      {/* Main Input Form Card */}
      <div className="w-full max-w-xl mx-auto bg-white/85 dark:bg-surface-dark/75 backdrop-blur-xl border border-white/40 dark:border-surface-border p-6 sm:p-8 rounded-3xl shadow-2xl space-y-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          
          {/* Name & Gender */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-sm font-extrabold text-slate-900 dark:text-slate-100">First Name</label>
              <input 
                type="text" 
                id="name"
                name="name"
                autoComplete="given-name"
                required
                placeholder="e.g. Alex"
                value={formData.name}
                onChange={e => setFormData({...formData, name: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-surface-border bg-white dark:bg-slate-800 focus:ring-2 focus:ring-primary outline-none text-slate-900 dark:text-white text-sm"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-extrabold text-slate-900 dark:text-slate-100">Gender</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setFormData({...formData, gender: 'female'})}
                  className={`flex-1 py-3 rounded-xl border font-bold text-sm transition-all cursor-pointer ${formData.gender === 'female' ? 'bg-primary text-white border-primary shadow-md' : 'bg-slate-50 dark:bg-slate-800 border-gray-200 dark:border-surface-border text-slate-650 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700'}`}
                >
                  Female
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({...formData, gender: 'male'})}
                  className={`flex-1 py-3 rounded-xl border font-bold text-sm transition-all cursor-pointer ${formData.gender === 'male' ? 'bg-primary text-white border-primary shadow-md' : 'bg-slate-50 dark:bg-slate-800 border-gray-200 dark:border-surface-border text-slate-650 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700'}`}
                >
                  Male
                </button>
              </div>
            </div>
          </div>
 
          {/* Date of Birth */}
          <div className="space-y-2">
            <label className="text-sm font-extrabold text-slate-900 dark:text-slate-100">Date of Birth (Solar / Gregorian)</label>
            
            <div className="grid grid-cols-4 gap-3">
              <select 
                id="birthMonth"
                name="birthMonth"
                aria-label="Birth Month"
                value={formData.birthMonth} 
                onChange={e => setFormData({...formData, birthMonth: e.target.value})}
                className="col-span-1 w-full px-2 py-3 rounded-xl border border-gray-300 dark:border-surface-border bg-white dark:bg-slate-800 focus:ring-2 focus:ring-primary outline-none text-slate-900 dark:text-white text-sm"
              >
                {months.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
              <select 
                id="birthDay"
                name="birthDay"
                aria-label="Birth Day"
                value={formData.birthDay} 
                onChange={e => setFormData({...formData, birthDay: e.target.value})}
                className="col-span-1 w-full px-2 py-3 rounded-xl border border-gray-300 dark:border-surface-border bg-white dark:bg-slate-800 focus:ring-2 focus:ring-primary outline-none text-slate-900 dark:text-white text-sm"
              >
                {days.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
              <select 
                id="birthYear"
                name="birthYear"
                aria-label="Birth Year"
                value={formData.birthYear} 
                onChange={e => setFormData({...formData, birthYear: e.target.value})}
                className="col-span-2 w-full px-3 py-3 rounded-xl border border-gray-300 dark:border-surface-border bg-white dark:bg-slate-800 focus:ring-2 focus:ring-primary outline-none text-slate-900 dark:text-white text-sm"
              >
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-4 gap-3 text-center text-xs font-bold text-slate-500 dark:text-slate-400">
              <span className="col-span-1">Month</span>
              <span className="col-span-1">Day</span>
              <span className="col-span-2">Year</span>
            </div>
          </div>
 
          {/* Time of Birth */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-extrabold text-slate-900 dark:text-slate-100 flex items-center gap-1">
                <span>Time of Birth</span>
                <span className="text-[10px] bg-primary/10 text-primary py-0.5 px-2 rounded-md font-bold">Recommended</span>
              </label>
              <label className="flex items-center gap-1.5 text-xs font-bold text-slate-650 dark:text-slate-400 cursor-pointer select-none hover:text-primary transition-colors">
                <input
                  type="checkbox"
                  id="timeUnknown"
                  name="timeUnknown"
                  checked={timeUnknown}
                  onChange={(e) => setTimeUnknown(e.target.checked)}
                  className="rounded border-gray-300 dark:border-slate-700 text-primary focus:ring-primary size-4 cursor-pointer"
                />
                <span>Unknown Time</span>
              </label>
            </div>
            
            {!timeUnknown && (
              <div className="grid grid-cols-4 gap-2 animate-fade-in">
                <select 
                  id="birthHour"
                  name="birthHour"
                  aria-label="Birth Hour"
                  value={timeParts.hour} 
                  onChange={e => setTimeParts({...timeParts, hour: e.target.value})}
                  className="col-span-1 w-full px-2 py-3 rounded-xl border border-gray-300 dark:border-surface-border bg-white dark:bg-slate-800 focus:ring-2 focus:ring-primary outline-none text-slate-900 dark:text-white text-sm"
                >
                  {Array.from({length: 12}, (_, i) => i + 1).map(h => <option key={h} value={h}>{h}</option>)}
                </select>
                <select 
                  id="birthMinute"
                  name="birthMinute"
                  aria-label="Birth Minute"
                  value={timeParts.minute} 
                  onChange={e => setTimeParts({...timeParts, minute: e.target.value})}
                  className="col-span-1 w-full px-2 py-3 rounded-xl border border-gray-300 dark:border-surface-border bg-white dark:bg-slate-800 focus:ring-2 focus:ring-primary outline-none text-slate-900 dark:text-white text-sm"
                >
                  {['00', '15', '30', '45'].map(m => <option key={m} value={m}>{m}</option>)}
                </select>
                <select 
                  id="birthPeriod"
                  name="birthPeriod"
                  aria-label="Birth Period (AM/PM)"
                  value={timeParts.period} 
                  onChange={e => setTimeParts({...timeParts, period: e.target.value})}
                  className="col-span-2 w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-surface-border bg-white dark:bg-slate-800 focus:ring-2 focus:ring-primary outline-none text-slate-900 dark:text-white font-bold text-sm"
                >
                  <option value="AM">AM</option>
                  <option value="PM">PM</option>
                </select>
              </div>
            )}
          </div>
 
          {/* Report Language Selector */}
          <div className="space-y-2">
            <label className="text-sm font-extrabold text-slate-900 dark:text-slate-100 flex items-center gap-1">
              <span>Report Language</span>
            </label>
            <select
              id="reportLanguage"
              name="reportLanguage"
              aria-label="Report Language"
              value={formData.reportLanguage || 'English'}
              onChange={e => setFormData({...formData, reportLanguage: e.target.value})}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-surface-border bg-white dark:bg-slate-800 focus:ring-2 focus:ring-primary outline-none text-slate-900 dark:text-white font-bold text-sm"
            >
              <option value="English">🇺🇸/🇬🇧 English</option>
              <option value="Turkish">🇹🇷 Turkish (Türkçe)</option>
              <option value="Dutch">🇳🇱 Dutch (Nederlands)</option>
              <option value="German">🇩🇪 German (Deutsch)</option>
              <option value="Italian">🇮🇹 Italian (Italiano)</option>
              <option value="Azerbaijani">🇦🇿 Azerbaijani (Azərbaycanca)</option>
              <option value="French">🇫🇷 French (Français)</option>
            </select>
          </div>
 
          {/* Privacy Consent Agreement Checkbox */}
          {session && !session.user?.is_anonymous && dbUser && (dbUser.credits || 0) >= 40 && (
            <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/40 border border-gray-150 dark:border-slate-800/50 space-y-3">
              <label className="flex items-start gap-3 cursor-pointer select-none text-xs text-slate-650 dark:text-slate-400 font-medium">
                <input
                  type="checkbox"
                  id="consentChecked"
                  name="consentChecked"
                  checked={consentChecked}
                  onChange={(e) => setConsentChecked(e.target.checked)}
                  className="mt-0.5 rounded border-gray-300 dark:border-slate-700 text-primary focus:ring-primary size-4 cursor-pointer"
                />
                <div className="space-y-1">
                  <span className="font-extrabold text-slate-800 dark:text-slate-200">
                    Consent to Saju Data Collection & Storage
                  </span>
                  <p className="text-[11px] leading-relaxed text-slate-550 dark:text-slate-400">
                    We collect and store your birth details to calculate your astrological coordinates. Saved reports are stored securely in your dashboard for future viewing.
                  </p>
                </div>
              </label>
            </div>
          )}

          <div className="flex flex-col gap-2">
            <button 
              type="submit"
              disabled={isLoading}
              className="w-full py-4 mt-4 bg-gradient-to-r from-primary via-purple-600 to-pink-600 hover:from-primary-dark hover:to-purple-700 text-white rounded-2xl font-black text-base sm:text-lg shadow-xl shadow-primary/20 hover:shadow-2xl hover:shadow-primary/30 transition-all duration-300 active:scale-[0.98] disabled:opacity-70 cursor-pointer flex items-center justify-center gap-2"
            >
              <Sparkles className="w-5 h-5" />
              <span>
                {session && dbUser && (dbUser.credits || 0) >= 40 
                  ? 'Calculate Cosmic Blueprint (Premium)' 
                  : 'Calculate Free Cosmic Blueprint'}
              </span>
            </button>
            <p className="text-center text-[11px] text-slate-500 dark:text-slate-400 mt-2">
              🔒 100% Secure & Confidential. Based on astronomical solar equations.
            </p>
          </div>
        </form>
      </div>

      {/* Interactive Element Explorer Section */}
      <section className="bg-white/70 dark:bg-surface-dark/70 backdrop-blur-xl border border-white/40 dark:border-surface-border p-6 sm:p-10 rounded-3xl shadow-xl space-y-8">
        <div className="text-center space-y-2 max-w-2xl mx-auto">
          <span className="text-xs font-black text-primary uppercase tracking-wider bg-primary/10 px-3 py-1 rounded-full">Interactive Theory</span>
          <h2 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white">
            The Five Elemental Archetypes (오행)
          </h2>
          <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-350">
            Click on any element below to explore how its cosmic frequency influences character, career, and vitality.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-2 sm:gap-4">
          {[
            { id: 'wood', label: 'Wood (목)', icon: <TreePine className="w-4 h-4" />, color: 'hover:border-emerald-500' },
            { id: 'fire', label: 'Fire (화)', icon: <Flame className="w-4 h-4" />, color: 'hover:border-rose-500' },
            { id: 'earth', label: 'Earth (토)', icon: <Mountain className="w-4 h-4" />, color: 'hover:border-amber-500' },
            { id: 'metal', label: 'Metal (금)', icon: <ShieldAlert className="w-4 h-4" />, color: 'hover:border-slate-400' },
            { id: 'water', label: 'Water (수)', icon: <Droplets className="w-4 h-4" />, color: 'hover:border-blue-500' },
          ].map((el) => (
            <button
              key={el.id}
              onClick={() => setActiveElementInfo(el.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl text-xs sm:text-sm font-extrabold transition-all cursor-pointer border ${
                activeElementInfo === el.id
                  ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900 shadow-md scale-105 border-transparent'
                  : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-gray-200 dark:border-surface-border'
              }`}
            >
              {el.icon}
              <span>{el.label}</span>
            </button>
          ))}
        </div>

        <div className="p-6 rounded-2xl bg-slate-50/80 dark:bg-slate-800/50 border border-slate-200 dark:border-surface-border space-y-3 animate-fade-in">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-gray-200 dark:border-surface-border/50 pb-3">
            <h3 className={`text-xl font-black ${elementDetails[activeElementInfo].color} flex items-center gap-2`}>
              <span>{elementDetails[activeElementInfo].title}</span>
              <span className="text-sm font-normal text-slate-400">{elementDetails[activeElementInfo].hanja}</span>
            </h3>
            <span className="text-xs font-bold text-slate-600 dark:text-slate-350 bg-white dark:bg-slate-900 py-1 px-3 rounded-full border border-gray-200 dark:border-surface-border">
              {elementDetails[activeElementInfo].keywords}
            </span>
          </div>
          <p className="text-sm sm:text-base leading-relaxed text-slate-700 dark:text-slate-300">
            {elementDetails[activeElementInfo].desc}
          </p>
          <div className="p-3 rounded-xl bg-white dark:bg-slate-900/90 text-xs font-semibold text-primary border border-primary/20">
            💼 {elementDetails[activeElementInfo].traits}
          </div>
        </div>
      </section>

      {/* Deep Educational Architecture Section (AdSense Content Anchor) */}
      <section className="space-y-8">
        <div className="text-center space-y-2 max-w-2xl mx-auto">
          <span className="text-xs font-black text-purple-500 uppercase tracking-wider bg-purple-500/10 px-3 py-1 rounded-full">Core Principles</span>
          <h2 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white">
            How Cosmic Saju Decodes Your Destiny
          </h2>
          <p className="text-xs sm:text-sm text-slate-650 dark:text-slate-350">
            Our dual-layer calculation combines rigorous solar mechanics with neural AI synthesis.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <article className="p-6 rounded-3xl bg-white/75 dark:bg-surface-dark/75 backdrop-blur-xl border border-white/40 dark:border-surface-border space-y-4 shadow-lg">
            <div className="size-12 rounded-2xl bg-indigo-500/10 text-indigo-500 flex items-center justify-center font-bold">
              <Orbit className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 dark:text-white">
              1. True Solar Time Correction
            </h3>
            <p className="text-xs sm:text-sm leading-relaxed text-slate-650 dark:text-slate-350">
              Unlike standard apps that use plain clock time, Cosmic Saju applies the Equation of Time (균시차) and geographical longitude adjustment to calculate the authentic solar angle at your exact minute of birth.
            </p>
          </article>

          <article className="p-6 rounded-3xl bg-white/75 dark:bg-surface-dark/75 backdrop-blur-xl border border-white/40 dark:border-surface-border space-y-4 shadow-lg">
            <div className="size-12 rounded-2xl bg-purple-500/10 text-purple-500 flex items-center justify-center font-bold">
              <BrainCircuit className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 dark:text-white">
              2. Multi-Dimensional Synthesis
            </h3>
            <p className="text-xs sm:text-sm leading-relaxed text-slate-650 dark:text-slate-350">
              We analyze the interaction of the Daymaster with the Ten Gods (십신), Twelve Earthly Branches (지지), and special combinations (Samhap, Banghap, Chung) to map out your complete energetic ecosystem.
            </p>
          </article>

          <article className="p-6 rounded-3xl bg-white/75 dark:bg-surface-dark/75 backdrop-blur-xl border border-white/40 dark:border-surface-border space-y-4 shadow-lg">
            <div className="size-12 rounded-2xl bg-pink-500/10 text-pink-500 flex items-center justify-center font-bold">
              <SunMoon className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 dark:text-white">
              3. Modern Actionable Guidance
            </h3>
            <p className="text-xs sm:text-sm leading-relaxed text-slate-650 dark:text-slate-350">
              Ancient destiny poetry is translated into modern career paths, financial timing, emotional regulation advice, and relationship compatibility recommendations you can execute today.
            </p>
          </article>
        </div>
      </section>

      {/* Saju vs Western Comparison Overview */}
      <section className="bg-gradient-to-br from-slate-900 to-indigo-950 text-white p-8 sm:p-12 rounded-3xl shadow-2xl space-y-6">
        <div className="max-w-2xl space-y-2">
          <span className="text-xs font-black text-primary-light uppercase tracking-wider bg-primary/20 px-3 py-1 rounded-full text-indigo-300">Comparative Astrology</span>
          <h2 className="text-2xl sm:text-3xl font-black">
            Why Eastern Saju Complements Western Astrology
          </h2>
          <p className="text-sm text-slate-300 leading-relaxed">
            While Western Zodiac examines distant constellations, Saju observes the solar climates of our immediate Earth environment. Discover how understanding your Daymaster and Five Elements gives you tactical clarity.
          </p>
        </div>

        <div className="pt-4 flex flex-wrap gap-4 items-center">
          <Link
            to="/saju-guide"
            className="px-6 py-3 bg-white text-slate-900 hover:bg-slate-100 rounded-xl font-bold text-sm transition-all flex items-center gap-2 shadow-lg"
          >
            <BookOpen className="w-4 h-4 text-primary" />
            <span>Read Complete Saju Encyclopedia</span>
            <ChevronRight className="w-4 h-4" />
          </Link>
          <Link
            to="/about"
            className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl font-bold text-sm transition-all border border-white/20"
          >
            Learn About Our Methodology
          </Link>
        </div>
      </section>

      {/* Real Trust & User Testimonial Preview */}
      <section className="space-y-6">
        <div className="text-center space-y-2 max-w-xl mx-auto">
          <h2 className="text-2xl font-black text-slate-900 dark:text-white">
            Trusted by Seekers Worldwide
          </h2>
          <p className="text-xs sm:text-sm text-slate-650 dark:text-slate-350">
            See how individuals and professionals utilize Cosmic Saju for life direction.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="p-5 rounded-2xl bg-white/60 dark:bg-surface-dark/60 border border-slate-200 dark:border-surface-border space-y-3">
            <div className="flex text-amber-400 text-sm">★★★★★</div>
            <p className="text-xs text-slate-650 dark:text-slate-350 leading-relaxed">
              "The depth of the Daymaster analysis was chillingly accurate. It explained career roadblocks I had battled for years."
            </p>
            <div className="text-xs font-bold text-slate-900 dark:text-white">— Sarah K., Product Designer (London)</div>
          </div>

          <div className="p-5 rounded-2xl bg-white/60 dark:bg-surface-dark/60 border border-slate-200 dark:border-surface-border space-y-3">
            <div className="flex text-amber-400 text-sm">★★★★★</div>
            <p className="text-xs text-slate-650 dark:text-slate-350 leading-relaxed">
              "As someone who studied Western astrology for a decade, Saju's 10-Year Daewun timing gave me the exact dates for my career transition."
            </p>
            <div className="text-xs font-bold text-slate-900 dark:text-white">— Michael T., Tech Founder (San Francisco)</div>
          </div>

          <div className="p-5 rounded-2xl bg-white/60 dark:bg-surface-dark/60 border border-slate-200 dark:border-surface-border space-y-3">
            <div className="flex text-amber-400 text-sm">★★★★★</div>
            <p className="text-xs text-slate-650 dark:text-slate-350 leading-relaxed">
              "The Five Elements balance section helped me understand why I felt so drained in high-pressure corporate environments."
            </p>
            <div className="text-xs font-bold text-slate-900 dark:text-white">— Elena R., Consultant (Berlin)</div>
          </div>
        </div>
      </section>

      {/* Comprehensive Knowledge Base & Editorial Index for Webmaster Quality Standards */}
      <section className="bg-white/80 dark:bg-surface-dark/80 backdrop-blur-xl border border-white/40 dark:border-surface-border p-8 sm:p-12 rounded-3xl shadow-xl space-y-10">
        <div className="space-y-3">
          <span className="text-xs font-black text-primary uppercase tracking-wider bg-primary/10 px-3 py-1 rounded-full">Complete Knowledge Base</span>
          <h2 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white">
            The Fundamental Architecture of Four Pillars (사주명리학)
          </h2>
          <p className="text-sm text-slate-650 dark:text-slate-350 leading-relaxed">
            Korean Saju (BaZi) is built upon centuries of systematic observation of astronomical cycles, seasonal transitions, and energetic balance. Explore the core building blocks that formulate your astrological identity:
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="space-y-4">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <Compass className="w-5 h-5 text-primary" />
              The 10 Heavenly Stems (천간 / Cheongan)
            </h3>
            <p className="text-xs sm:text-sm text-slate-650 dark:text-slate-350 leading-relaxed">
              Heavenly Stems represent the purest celestial impulses. Divided into Yin and Yang for each of the Five Elements:
            </p>
            <ul className="space-y-2 text-xs sm:text-sm text-slate-700 dark:text-slate-300">
              <li><strong className="text-emerald-500">Jap (甲) & Eul (乙):</strong> Yang & Yin Wood — Visionary leadership and resilient adaptability.</li>
              <li><strong className="text-rose-500">Byeong (丙) & Jeong (丁):</strong> Yang & Yin Fire — Radiant charisma and focused intuitive guidance.</li>
              <li><strong className="text-amber-500">Mu (戊) & Gi (己):</strong> Yang & Yin Earth — Rock-solid stability and nurturing resource management.</li>
              <li><strong className="text-slate-400">Gyeong (庚) & Sin (辛):</strong> Yang & Yin Metal — Unyielding structural reform and refined perfectionism.</li>
              <li><strong className="text-blue-500">Im (壬) & Gye (癸):</strong> Yang & Yin Water — Ocean-deep strategy and perceptive emotional wisdom.</li>
            </ul>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <Orbit className="w-5 h-5 text-purple-500" />
              The 12 Earthly Branches (지지 / Jiji)
            </h3>
            <p className="text-xs sm:text-sm text-slate-650 dark:text-slate-350 leading-relaxed">
              Earthly Branches represent the physical earthly climate and the 12 archetypal animal energies that anchor our temporal reality:
            </p>
            <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm text-slate-700 dark:text-slate-300">
              <div>• Rat (子) — Water</div>
              <div>• Ox (丑) — Earth</div>
              <div>• Tiger (寅) — Wood</div>
              <div>• Rabbit (卯) — Wood</div>
              <div>• Dragon (辰) — Earth</div>
              <div>• Snake (巳) — Fire</div>
              <div>• Horse (午) — Fire</div>
              <div>• Goat (未) — Earth</div>
              <div>• Monkey (申) — Metal</div>
              <div>• Rooster (酉) — Metal</div>
              <div>• Dog (戌) — Earth</div>
              <div>• Pig (亥) — Water</div>
            </div>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">
              Each branch holds specific hidden stems (지장간) that reveal subconscious motivations and secondary personality traits.
            </p>
          </div>
        </div>

        <hr className="border-gray-200 dark:border-surface-border" />

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2">
          <div className="text-xs text-slate-500 dark:text-slate-400">
            Want to learn more about advanced combinations like Samhap (삼합) and Banghap (방합)?
          </div>
          <Link
            to="/saju-guide"
            className="px-5 py-2.5 bg-primary/10 hover:bg-primary/20 text-primary rounded-xl font-bold text-xs transition-all flex items-center gap-1.5"
          >
            <span>Visit Complete Encyclopedia</span>
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

    </div>
  );
};

export default Dashboard;

